import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { graphql, Link } from 'gatsby';
import { css } from '@emotion/react';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FullImage from '../components/full-image';
import Button from '../components/button';
import Information from '../components/information';
import PortableText from '../components/portable-text';
import PDF from '../components/pdf';
import Gallery from '../components/gallery';
import Accordion from '../components/accordion';
import ModalContent from '../components/modal-content';
import { formatDates } from '../utility';

export default function Support({ data }) {
  const theme = useContext(ThemeContext);
  const volunteerJobs = (data.support.work || [])
    .filter((job) => ((!job.closeDate) || (new Date().getTime() - 86400000 < new Date(job.closeDate).getTime())));

  useLayoutEffect(() => {
    theme.setGrayFooter(false);
  }, []);

  function dateDiff(d1, d2) {
    return (d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24);
  }

  return (
    <div
      css={css`
        background-color: var(--white);
      `}
    >
      <SEO
        title="Support"
        description={(data.support._rawContent) ? data.support._rawContent.map(el => el.children).flat().map((el) => el?.text)[0] : null}
        imageID={(data.support.featureImage) ? data.support.featureImage.asset._id : null}
      />
      <div
        css={css`
          background-color: var(--primary);
        `}
      >
        <FullImage
          text={`
            Support/
          `}
          includeTitle
          image={data.support.featureImage}
        />
      </div>
      <div
        className="type--45"
        css={css`
          padding: 0 var(--margin);
          margin-bottom: var(--margin);
        `}
      >
        <PortableText
          blocks={data.support._rawContent}
        />
      </div>
      <div
        css={css`
          margin: 0 var(--margin);
          margin-bottom: var(--bigMargin);
        `}
      >
        <Button
          text="Donate today"
          link={data.support.donateLink}
          large
          external
        />
      </div>
      <div
        css={css`
          padding: 0 var(--margin);
          margin-bottom: var(--bigMargin);
        `}
      >
        {data.support.information.length > 0 && data.support.information
          .map((information, i) => (
            <Accordion
              key={i}
              title={information.title}
            >
              <PortableText
                blocks={information._rawContent}
              />
            </Accordion>
          ))
        }
        <Accordion
          title="Major Donors"
        >
          <div
            css={css`
              columns: 2;
              column-gap: var(--gutter);
              column-fill: balance-all;
              text-transform: uppercase;

              a {
                text-decoration: none;
              }
            `}
          >
            {data.support.majorDonors.length > 0 && data.support.majorDonors
              .map((donor, i) => (
                <div
                  key={i}
                >
                  {donor._type === "donor" && <span>{donor.name}</span>}
                  {donor._type === "donorProfile" && <Link
                    to={`/donor/${donor.slug.current}`}
                  >{donor.name}</Link>}
                </div>
              ))
            }
          </div>
        </Accordion>
      </div>
      {volunteerJobs.length > 0 && <div
        css={css`
          color: var(--white);
          background-image: linear-gradient(to bottom, transparent, var(--black) 15rem, var(--black) calc(100% - 15rem), transparent);
          padding: 20rem var(--margin);
        `}
      >
        <h2
          className="type--70"
          css={css`
            margin-top: 5rem;
            margin-bottom: 5rem;
          `}
        >Volunteer/</h2>
        <div
          className="type--45"
          css={css`
            display: grid;
            grid-template-columns: repeat(12, 1fr);
            gap: var(--gutter);
            margin-bottom: var(--bigMargin);
          `}
        >
          {volunteerJobs
            .map((job, i) => {
              let color = 'var(--green)';
              let status = 'Applications now open';

              if (job.closeDate) {
                const daysUntil = dateDiff(new Date(job.closeDate), new Date());

                if (daysUntil <= 7) {
                  color = 'var(--orange)';
                  status = `Applications closing ${new Date(job.closeDate).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}`;
                } else {
                  color = 'var(--red)';
                  status = `Applications closing soon, ${new Date(job.closeDate).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}`;
                }
              }

              return (
                <div
                  key={i}
                  id={job.slug.current}
                  css={css`
                    grid-column: span 6;
                    padding: var(--gutter);
                    border: 2px solid var(--white);

                    @media (max-width: 700px) {
                      grid-column: span 12;
                    }

                    a {
                      color: var(--white);

                      &:hover {
                        color: var(--primary);
                      }
                    }
                  `}
                >
                  <div
                    className="type--20"
                    css={css`
                      color: ${color};
                      margin-bottom: calc(var(--gutter) * 0.75);

                      &::before {
                        content: '';
                        display: inline-block;
                        width: 1.1rem;
                        height: 1.1rem;
                        border-radius: 50%;
                        background-color: ${color};
                        margin-right: 1rem;
                      }
                    `}
                  >
                    {status}
                  </div>
                  <div
                    css={css`
                      margin-bottom: var(--margin);
                    `}
                  ><span
                    css={css`
                    text-transform: uppercase;
                    `}
                  >{job.title}</span>: {job.description}</div>
                  {job.email && <div
                    className="type--20"
                  >
                    <a target="_blank" rel="noreferrer" href={`mailto:${job.email}`}>Email Us</a>
                  </div>}
                  {job.links.length > 0 && job.links
                    .map((link, i) => (
                      <div
                        className="type--20"
                        css={css`
                          margin-top: calc(var(--gutter) * 0.5);
                        `}
                      >
                        <a target="_blank" rel="noreferrer" href={link.url}>{link.title}</a>
                      </div>
                    ))
                  }
                  {job.files.length > 0 && job.files
                    .map((file, i) => (
                      <div
                        className="type--20"
                        css={css`
                          margin-top: calc(var(--gutter) * 0.5);
                        `}
                      >
                        <a target="_blank" rel="noreferrer" href={file.file.asset.url}>{file.title}</a>
                      </div>
                    ))
                  }
                </div>
            )})
          }
        </div>
        <div
          className="type--20"
        >
          {data.support.fileText}
        </div>
        <div
          css={css`
            margin-bottom: var(--margin);
          `}
        >
          {data.support.files.length > 0 && data.support.files
            .map((pdf, i) => (
              <PDF
                key={i}
                link={pdf.file.asset.url}
                text={pdf.title}
              />
            ))
          }
        </div>
      </div>}
    </div>
  );
}

export const query = graphql`
  query {
    support: sanitySupport(_id: { regex: "/(drafts\\\\.)?support/" }) {
      id
      featureImage {
        ...Image
        asset {
          description
        }
      }
      _rawContent(resolveReferences: {maxDepth: 10})
      donateLink
      information {
        title
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      majorDonors {
        ... on SanityDonor {
          _type
          name
        }
        ... on SanityProfile {
          _type
          name
          slug {
            current
          }
        }
      }
      work {
        closeDate
        title
        slug {
          current
        }
        description
        email
        links {
          title
          url
        }
        files {
          title
          file {
            asset {
              url
            }
          }
        }
      }
      fileText
      files {
        title
        file {
          asset {
            url
          }
        }
      }
    }
  }
`

